// Tiny JSONPath-ish evaluator for the response-body filter bar.
// Supports the practical subset people actually type:
//   $                whole document
//   $.a.b            object keys
//   $.items[0]       array index (negative counts from the end)
//   $.items[*].id    wildcard over arrays AND objects (maps + flattens)
//   $["weird key"]   bracket access for keys that aren't identifiers
//   a.b              leading $ optional
// Returns either the resolved value or a human error for the hint row.

export type JsonPathResult =
  | { ok: true; value: unknown; matches: number }
  | { ok: false; error: string };

type Token =
  | { kind: "key"; key: string }
  | { kind: "index"; index: number }
  | { kind: "wild" };

function tokenize(path: string): Token[] | string {
  let p = path.trim();
  if (p.startsWith("$")) p = p.slice(1);
  const tokens: Token[] = [];
  const re = /^(?:\.\.?|)\s*(?:([A-Za-z_$][\w$-]*)|\[\s*(-?\d+)\s*\]|\[\s*"((?:[^"\\]|\\.)*)"\s*\]|\[\s*'((?:[^'\\]|\\.)*)'\s*\]|\[\s*\*\s*\]|(\*))/;
  while (p.length) {
    // Allow separators between tokens.
    if (p.startsWith(".")) { /* handled by the regex */ }
    const m = re.exec(p);
    if (!m) return `Can't parse path at "${p.slice(0, 12)}…"`;
    if (m[1] !== undefined) {
      if (m[1] === "$") return "Unexpected $ mid-path";
      tokens.push({ kind: "key", key: m[1] });
    } else if (m[2] !== undefined) tokens.push({ kind: "index", index: Number(m[2]) });
    else if (m[3] !== undefined) tokens.push({ kind: "key", key: m[3].replace(/\\(.)/g, "$1") });
    else if (m[4] !== undefined) tokens.push({ kind: "key", key: m[4].replace(/\\(.)/g, "$1") });
    else tokens.push({ kind: "wild" });
    p = p.slice(m[0].length);
  }
  return tokens;
}

export function evalJsonPath(root: unknown, path: string): JsonPathResult {
  const trimmed = path.trim();
  if (!trimmed || trimmed === "$") return { ok: true, value: root, matches: 1 };
  const tokens = tokenize(trimmed);
  if (typeof tokens === "string") return { ok: false, error: tokens };

  // Wildcards fan out: track a list of current values, map each token
  // across all of them, and flatten.
  let fanned = false;
  let current: unknown[] = [root];
  for (const t of tokens) {
    const next: unknown[] = [];
    for (const v of current) {
      if (v === null || v === undefined) continue;
      if (t.kind === "key") {
        if (typeof v === "object" && !Array.isArray(v) && t.key in (v as Record<string, unknown>)) {
          next.push((v as Record<string, unknown>)[t.key]);
        }
      } else if (t.kind === "index") {
        if (Array.isArray(v)) {
          const i = t.index < 0 ? v.length + t.index : t.index;
          if (i >= 0 && i < v.length) next.push(v[i]);
        }
      } else {
        fanned = true;
        if (Array.isArray(v)) next.push(...v);
        else if (typeof v === "object") next.push(...Object.values(v as Record<string, unknown>));
      }
    }
    current = next;
    if (current.length === 0) return { ok: false, error: "No match for that path" };
  }

  if (fanned) return { ok: true, value: current, matches: current.length };
  return { ok: true, value: current[0], matches: current.length };
}
